import _ from 'lodash';
import React, { useRef, useState, useEffect } from 'react';
import { Container, Row, Col, Card, Modal, Form } from 'react-bootstrap';
import { getExams, getLatestExam, getExamImage, addExam, deleteExam, addExamImage, deleteExamImage } from '@/api/index';
import { Btn, FTxt, FNum, FDate, Ic } from '@/components/basic/index';
import { FItemGroup, DateFormat, Toast } from '@/components/shared/index';
import ConfirmModal from '@/components/page-parts/ConfirmModal';
import style from '@/pages/report/Report.scss';
import { Exam, ExamSubject } from '@/entity/Report';
import { Validator, Rule } from '@/util/validate';
import dayjs from 'dayjs';
import { isEmpty } from '@/util/util';
import ImageModal from './ImageModal';
import EditModal from './EditModal';
import { isGoal } from '@/util/company';

const Exams = ({ studentId }) => {
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', content: '', type: '', buttonTitle: '' });
  const [image, setImage] = useState({ show: false, file: null, title: null });
  const [addFile, setAddfile] = useState({ name: '', mime: '', file: '' });
  const [uploadFile, setUploadFile] = useState([]);
  const [exams, setExams] = useState([]);
  const [exam, setExam] = useState(new Exam());
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddExamModal, setShowAddExamModal] = useState(false);
  const [examId, setExamId] = useState(null);
  const [fileId, setFileId] = useState(null);
  const inputRef = useRef(null);

  const tests = ['単元テスト', '中間テスト', '期末テスト', '実力テスト', '模試・診断'];

  const handleShowConfirm = (type, title, content, buttonTitle, examId, fileId) => {
    setConfirmModal({
      ...confirmModal,
      show: true,
      title: title,
      content: content,
      type: type,
      buttonTitle: buttonTitle,
    });
    setExamId(examId);
    setFileId(fileId);
  };
  const handleCloseConfirm = () => setConfirmModal({ ...confirmModal, show: false, title: '', content: '', type: '' });

  const handleCloseModal = () => {
    setAddfile({ name: '', mime: '', file: '' });
    setExamId(null);
    setUploadFile([]);
    setShowModal(false);
  };
  const handleShowModal = (id) => {
    setExamId(id);
    setShowModal(true);
    setErrors({});
  };

  const handleCloseAddExamModal = () => {
    setShowAddExamModal(false);
  };
  const handleShowAddExamModal = async () => {
    setErrors({});
    const name = isGoal() ? tests[0] : '';
    setExam(new Exam({ name }));
    await setLatestExamSubjects(studentId);
    setShowAddExamModal(true);
  };

  const validateFile = () => {
    const validate = new Validator(addFile);
    validate.exec('name', '画像名', [Rule.require()]);
    validate.exec('file', '画像', [Rule.require({ type: 'file' })]);
    setErrors(validate.getErrors());
    return validate.inValid();
  };

  const validateExam = () => {
    const validate = new Validator(exam);
    validate.exec('date', 'テスト実施日', [Rule.require()]);
    validate.exec('name', 'テスト名', [Rule.require()]);
    exam.examSubjects.forEach((_, i) => {
      validate.exec(`examSubjects[${i}].subjectName`, '科目名', [Rule.require()]);
      validate.exec(`examSubjects[${i}].score`, '点数', [Rule.require(), Rule.num(), Rule.numRange(0, 999)]);
      validate.exec(`examSubjects[${i}].maxScore`, '最大点数', [
        Rule.require(),
        Rule.num(),
        Rule.numRange(0, 999),
        Rule.custom((value) => {
          const compareValue = exam.examSubjects[i].score;
          if (isEmpty(compareValue) || isEmpty(value)) return false;
          if (Number(compareValue) > Number(value)) return true;
        }, '最大は点数より大きな値を入力してください。'),
      ]);
    });
    setErrors(validate.getErrors());
    return validate.inValid();
  };

  const removeFile = () => {
    setUploadFile([]);
    setAddfile({ ...addFile, name: '', mime: '', file: '' });
  };

  const clickFileUpload = () => {
    inputRef.current.click();
  };

  const deleteFile = async () => {
    await deleteExamImage(studentId, examId, fileId);
    handleCloseConfirm();
    Toast.success('画像を削除しました');
    fillExams(studentId);
  };

  const onFileInputChange = (newFile) => {
    setLoading(true);
    if (newFile.target.files == null) return;
    const reader = new FileReader();
    const file = newFile.target.files[0];
    setUploadFile((current) => current.concat(file));
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const dataURI = reader.result;
      const base64Data = dataURI.split(',')[1] || dataURI;
      setAddfile({ ...addFile, name: '', mime: file.type, file: base64Data });
      setLoading(false);
    };
    if (inputRef.current) inputRef.current.value = '';
  };

  const showImage = async (examId, imageId, name, mime) => {
    const data = await getExamImage(studentId, examId, imageId);
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result;
      setImage({ ...image, show: true, file: b64.replace('application/octet-stream', mime), title: name });
    };
    reader.readAsDataURL(data);
  };

  const closeImage = () => setImage({ ...image, show: false, file: null, title: null });

  const fillExams = async (id) => {
    const data = await getExams(id);
    setExams(data);
  };

  const setLatestExamSubjects = async (id) => {
    const d = await getLatestExam(id);
    if (!d) return;
    setExam(() => new Exam(d, true));
  };

  const storeExam = async () => {
    if (validateExam()) return;
    try {
      await addExam(studentId, exam);
      await fillExams(studentId);
      Toast.success('試験を追加しました');
      handleCloseAddExamModal();
    } catch (e) {
      console.error(e);
    }
  };

  const onDeleteExam = async (ex) => {
    const day = dayjs(ex.date);
    const dateStr = day.isValid() ? day.format('YYYY年MM月DD日') : ex.date;
    const ok = window.confirm(`${dateStr}のテストを削除してもいいですか？`);
    if (!ok) return;
    try {
      await deleteExam(studentId, ex.id);
      await fillExams(studentId);
      Toast.success('テストを削除しました');
    } catch (e) {
      console.error(e);
      Toast.error('削除に失敗しました');
    }
  };

  const newRowExamSubject = () => {
    exam.examSubjects.push(new ExamSubject());
    setExam({ ...exam });
  };
  const removeRowExamSubject = (idx) =>
    setExam({ ...exam, examSubjects: exam.examSubjects.filter((_, i) => i !== idx) });

  const onAddExamImage = async () => {
    if (validateFile()) return;
    try {
      await addExamImage(studentId, examId, addFile);
      Toast.success('画像を追加しました');
    } catch (e) {
      console.error(e);
    }
    await fillExams(studentId);
    handleCloseModal();
  };

  useEffect(() => {
    fillExams(studentId);
  }, []);

  return (
    <Container fluid>
      <Row style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <Col style={{ textAlign: 'right' }}>
          <Btn children="新しいテスト" click={handleShowAddExamModal} color="success" prefixIcon="faPlus" />
        </Col>
      </Row>
      <div style={{ marginTop: '1rem' }}>
        {exams.length === 0
          ? '登録されているテストはありません'
          : exams.map((r, i) => {
              return (
                <div key={i + 'test-n'}>
                  <Card className={[style.card]}>
                    <Card.Title>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <DateFormat date={r.date} />
                        <a style={{ fontSize: '0.85rem', color: '#0d6efd' }} onClick={() => onDeleteExam(r)}>
                          削除
                        </a>
                      </div>
                    </Card.Title>
                    <Card.Subtitle>{r.name}</Card.Subtitle>
                    <Card.Body>
                      <table className={style.scoreTable}>
                        <tbody>
                          {r.examSubjects.map((rr, ii) => {
                            return (
                              <React.Fragment key={ii + 'test'}>
                                <tr>
                                  <td className={style.subject}>{rr.subjectName}</td>
                                  <td style={{ fontSize: '0.5rem' }}>
                                    <span style={{ fontSize: '1.3rem', border: 'none' }}>{rr.score}</span>
                                    <span style={{ fontSize: '0.5rem', border: 'none', marginRight: '0.2rem' }}>
                                      /{rr.maxScore}
                                    </span>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                      <Row style={{ marginTop: '1rem' }}>
                        {r.examImages.map((rr, ii) => {
                          return (
                            <React.Fragment key={ii + 'exam'}>
                              <div className={style.imageLink}>
                                <a onClick={() => showImage(r.id, rr.id, rr.name, rr.mime)}>
                                  <Ic iName="faImage" />
                                  {rr.name}
                                </a>
                                <Btn
                                  prefixIcon="faTrashCan"
                                  variant
                                  color="danger"
                                  width="30"
                                  circle
                                  className="trash-btn"
                                  click={() => {
                                    handleShowConfirm(
                                      'danger',
                                      '削除確認',
                                      `${rr.name}を削除します。よろしいですか？`,
                                      '削除する',
                                      r.id,
                                      rr.id
                                    );
                                  }}
                                />
                              </div>
                            </React.Fragment>
                          );
                        })}
                        <Col>
                          <Btn
                            style={{ marginTop: '10px' }}
                            click={() => handleShowModal(r.id)}
                            children="新しい画像"
                            prefixIcon="faPlus"
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
      </div>
      <ImageModal show={image.show} title={image.title} file={image.file} onClose={closeImage} />
      <EditModal
        show={showAddExamModal}
        title="テスト"
        onClose={handleCloseAddExamModal}
        onRegister={storeExam}
        registerBtnDisabled={exam.examSubjects.length === 0}
      >
        <FItemGroup label="テスト実施日" className={style.spaceB} errors={errors} paths={['date']}>
          <Col>
            <FDate disabled={false} value={exam.date} change={(e) => setExam({ ...exam, date: e.target.value })} />
          </Col>
        </FItemGroup>
        <FItemGroup label="テスト名" className={style.spaceB} errors={errors} paths={['name']}>
          <Col>
            {isGoal() && (
              <>
                <Form.Select value={exam.name} onChange={(e) => setExam({ ...exam, name: e.target.value })}>
                  {_.map(tests, (t) => {
                    return (
                      <React.Fragment key={t}>
                        <option value={t}>{t}</option>
                      </React.Fragment>
                    );
                  })}
                </Form.Select>
              </>
            )}
            {!isGoal() && (
              <>
                <FTxt disabled={false} value={exam.name} change={(e) => setExam({ ...exam, name: e.target.value })} />
              </>
            )}
          </Col>
        </FItemGroup>
        {exam.examSubjects.map((r, i) => {
          return (
            <React.Fragment key={i.toString()}>
              <FItemGroup
                label="科目"
                className={style.spaceB}
                errors={errors}
                paths={[`examSubjects[${i}].subjectName`, `examSubjects[${i}].score`, `examSubjects[${i}].maxScore`]}
              >
                <Row>
                  <Col xs={10}>
                    <FTxt
                      disabled={false}
                      value={r.subjectName}
                      style={{ marginBottom: '0.2rem' }}
                      change={(e) => {
                        exam.examSubjects[i].subjectName = e.target.value;
                        setExam({ ...exam, examSubjects: exam.examSubjects });
                      }}
                    />
                  </Col>
                  <Col xs={1}>
                    <Btn
                      isCustom
                      outline
                      prefixIcon="faXmark"
                      click={() => removeRowExamSubject(i)}
                      width="30px"
                      height="30px"
                      className={style.xmarkButton}
                    />
                  </Col>
                </Row>
                <Col xs={1} className={style.spaceB}></Col>
                <Row>
                  <Col xs={5}>
                    <FNum
                      disabled={false}
                      suffix="点"
                      value={r.score}
                      change={(e) => {
                        exam.examSubjects[i].score = e.target.value;
                        setExam({ ...exam, examSubjects: exam.examSubjects });
                      }}
                    />
                  </Col>
                  <Col xs={1} className={style.slashSeparate}>
                    ／
                  </Col>
                  <Col xs={5}>
                    <FNum
                      disabled={false}
                      suffix="点"
                      value={r.maxScore}
                      change={(e) => {
                        exam.examSubjects[i].maxScore = e.target.value;
                        setExam({ ...exam, examSubjects: exam.examSubjects });
                      }}
                    />
                  </Col>
                </Row>
              </FItemGroup>
            </React.Fragment>
          );
        })}
        <Btn outline prefixIcon="faPlus" click={newRowExamSubject}>
          新しい科目を追加
        </Btn>
      </EditModal>
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>画像のアップロード</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addFile.file !== '' ? (
            <div style={{ display: 'inline', fontSize: '0.8rem' }}>
              <div>
                {uploadFile[0].name}
                <Btn
                  prefixIcon="faTrashCan"
                  variant
                  color="danger"
                  width="30"
                  circle
                  className="trash-btn"
                  click={() => removeFile()}
                />
              </div>
            </div>
          ) : (
            <FItemGroup errors={errors} paths={['file']}>
              <Col>
                <input
                  id="examFile"
                  type="file"
                  hidden
                  ref={inputRef}
                  onChange={onFileInputChange}
                  accept="image/*"
                ></input>
                <Btn outline click={!isLoading ? clickFileUpload : null} prefixIcon="faCamera">
                  {isLoading ? '読み込み中...' : '画像を選択'}
                </Btn>
              </Col>
            </FItemGroup>
          )}
          <FItemGroup label="画像名（例：国語問題用紙1）" className={style.spaceB} errors={errors} paths={['name']}>
            <Col>
              <FTxt
                disabled={false}
                value={addFile.name}
                change={(e) => setAddfile({ ...addFile, name: e.target.value })}
              />
            </Col>
          </FItemGroup>
        </Modal.Body>
        <Modal.Footer>
          <Btn
            click={async () => await onAddExamImage()}
            prefixIcon="faCloudArrowUp"
            children="アップロード"
            showLoading={true}
          />
        </Modal.Footer>
      </Modal>
      <ConfirmModal
        show={confirmModal.show}
        onClose={handleCloseConfirm}
        onSubmit={async () => await deleteFile()}
        title={confirmModal.title}
        content={confirmModal.content}
        footerButtonTitle={confirmModal.buttonTitle}
        showSubmitLoading={true}
      />
    </Container>
  );
};

export default Exams;
